import type { DashboardTableQueryParams } from './dashboardTable.types'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod, PeriodComparisonMetric } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface SchedulingOverviewGoals {
  scheduledAppointmentsGoal: number | null
  confirmationRateGoalPct: number | null
  noShowRateGoalPct: number | null
  openChairMinutesGoal: number | null
  newPatientsGoal: number | null
  hygieneUtilizationGoalPct: number | null
  sameDayTreatmentGoalPct: number | null
  pendingTreatmentValueGoal: number | null
}

export interface SchedulingOverviewSummary {
  scheduled: PeriodComparisonMetric
  confirmationRate: PeriodComparisonMetric
  noShowRate: PeriodComparisonMetric
  openChairMinutes: PeriodComparisonMetric
  newPatients: PeriodComparisonMetric
  hygieneUtilizationRate: PeriodComparisonMetric
  sameDayTreatmentRate: PeriodComparisonMetric
  pendingTreatmentValue: PeriodComparisonMetric
  sameDayCancellationRate: PeriodComparisonMetric
  sameDayCancellations: PeriodComparisonMetric
  sameDayCancellationTotalAppointments: PeriodComparisonMetric
  sameDayCancellationOpenChairMinutes: PeriodComparisonMetric
  appointmentOverrunRate: PeriodComparisonMetric
  appointmentOverruns: PeriodComparisonMetric
  appointmentOverrunCompleted: PeriodComparisonMetric
  appointmentOverrunAvgDelayMinutes: PeriodComparisonMetric
}

export interface SchedulingOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  previousPeriodStart: string
  previousPeriodEnd: string
  summary: SchedulingOverviewSummary
  goals: SchedulingOverviewGoals | null
}

export interface SchedulingOverviewQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
  operatoryCount?: number
}

export const getSchedulingOverview = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingOverviewResult> =>
  axios
    .get<
      ApiResponse<SchedulingOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/scheduling/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface SchedulingScheduledRow {
  id: string
  dateTime: string
  patientId: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  amount: string
  hasNotes: boolean
}

export interface SchedulingScheduledResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: SchedulingScheduledRow[]
}

export interface SchedulingConfirmationMix {
  confirmed: number
  unconfirmed: number
  cancelled: number
}

export interface SchedulingConfirmationRow {
  id: string
  patientId: string
  patientName: string
  provider: string
  status: 'Confirmed' | 'Unconfirmed' | 'Cancelled'
}

export interface SchedulingConfirmationResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  mix: SchedulingConfirmationMix
  rows: SchedulingConfirmationRow[]
}

export interface SchedulingOpenChairChartPoint {
  label: string
  hours: number
}

export interface SchedulingOpenChairTableRow {
  id: string
  provider: string
  utilization: string
  totalBooked: string
  openTime: string
  status: string
}

export interface SchedulingOpenChairResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  chart: {
    data: SchedulingOpenChairChartPoint[]
    xDomain: [number, number]
    xTicks: number[]
  }
  rows: SchedulingOpenChairTableRow[]
}

export interface SchedulingNoShowChartPoint {
  label: string
  actual: number
  projected: number
}

export interface SchedulingNoShowTableRow {
  id: string
  patient: string
  patientId: string
  lastVisit: string
  treatmentNeeded: string
  estimatedTime: string
  scheduleFit: string
  estProduction: string
  priority: string
}

export interface SchedulingNoShowResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  chart: {
    data: SchedulingNoShowChartPoint[]
    yDomain: [number, number]
    yTicks: number[]
    goalLine: number
  }
  rows: SchedulingNoShowTableRow[]
}

const schedulingDetailGet = <T>(
  path: string,
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/scheduling/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface SchedulingScheduledRowsResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: SchedulingScheduledRow[]
  count: number
}

export interface SchedulingConfirmationRowsResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: SchedulingConfirmationRow[]
  count: number
}

export const getSchedulingScheduled = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingScheduledResult> =>
  schedulingDetailGet('scheduled', params, options)

export const getSchedulingScheduledRows = (
  params: SchedulingOverviewQueryParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingScheduledRowsResult> =>
  schedulingDetailGet('scheduled/rows', params, options)

export const getSchedulingConfirmation = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingConfirmationResult> =>
  schedulingDetailGet('confirmation', params, options)

export const getSchedulingConfirmationRows = (
  params: SchedulingOverviewQueryParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingConfirmationRowsResult> =>
  schedulingDetailGet('confirmation/rows', params, options)

export const getSchedulingOpenChair = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingOpenChairResult> =>
  schedulingDetailGet('open-chair', params, options)

export const getSchedulingNoShow = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SchedulingNoShowResult> =>
  schedulingDetailGet('no-show', params, options)

export interface SameDayCancellationKpiData {
  cancellationRate: string
  cancellationRateGoal: string
  sameDayCancellations: number
  totalAppointments: number
  openChairTime: string
  lostProduction: string
  fillRate: string
  comparisonLabel: string
  rateDelta: string
  rateTrend: 'up' | 'down'
  cancellationsDelta: string
  cancellationsTrend: 'up' | 'down'
  lostProductionDelta: string
  lostProductionTrend: 'up' | 'down'
  fillRateDelta: string
  fillRateTrend: 'up' | 'down'
}

export interface SameDayCancellationChartPoint {
  label: string
  cancellations: number
  filled: number
  openChairHours: number
}

export interface SameDayCancellationChartConfig {
  data: SameDayCancellationChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface SameDayCancellationByProviderRow {
  id: string
  provider: string
  totalAppts: number
  cancellations: number
  cancellationRate: string
  filled: number
  fillRate: string
  lostProduction: string
}

export interface SameDayCancellationByTimePeriodRow {
  id: string
  timePeriod: string
  totalAppts: number
  cancellations: number
  cancellationRate: string
  filled: number
  fillRate: string
  lostProduction: string
}

export interface SameDayCancellationOverviewMetric {
  id: string
  leftLabel: string
  value: string
}

/** Card payload from GET .../same-day-cancellation/overview (flat KPIs, no nested kpis). */
export interface SameDayCancellationOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  cancellationRate: string
  cancellationRateGoal: string
  sameDayCancellations: number
  totalAppointments: number
  openChairTime: string
  metrics: SameDayCancellationOverviewMetric[]
  chart: SameDayCancellationChartConfig
}

export interface SameDayCancellationDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: SameDayCancellationKpiData
  chart: SameDayCancellationChartConfig
  byProvider: SameDayCancellationByProviderRow[]
  byTimePeriod: SameDayCancellationByTimePeriodRow[]
}

export interface AppointmentOverrunKpiData {
  overrunRate: string
  overrunRateGoal: string
  overrunAppointments: number
  completedAppointments: number
  avgDelay: string
  providersWithOverrun: number
  lostProduction: string
  comparisonLabel: string
  overrunRateDelta: string
  overrunRateTrend: 'up' | 'down'
  overrunDelta: string
  overrunTrend: 'up' | 'down'
  avgDelayDelta: string
  avgDelayTrend: 'up' | 'down'
  lostProductionDelta: string
  lostProductionTrend: 'up' | 'down'
}

export interface AppointmentOverrunChartPoint {
  label: string
  overruns: number
  onTime: number
}

export interface AppointmentOverrunChartConfig {
  data: AppointmentOverrunChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface AppointmentOverrunByProviderRow {
  id: string
  provider: string
  completed: number
  overruns: number
  overrunRate: string
  avgDelay: string
  maxDelay: string
  lostProduction: string
}

export interface AppointmentOverrunByTimePeriodRow {
  id: string
  timePeriod: string
  completed: number
  overruns: number
  overrunRate: string
  avgDelay: string
  lostProduction: string
}

export interface AppointmentOverrunDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: AppointmentOverrunKpiData
  chart: AppointmentOverrunChartConfig
  byProvider: AppointmentOverrunByProviderRow[]
  byTimePeriod: AppointmentOverrunByTimePeriodRow[]
}

export const getSameDayCancellationDetail = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SameDayCancellationDetailResult> =>
  schedulingDetailGet('same-day-cancellation', params, options)

export const getSameDayCancellationOverview = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<SameDayCancellationOverviewResult> =>
  schedulingDetailGet('same-day-cancellation/overview', params, options)

export const getAppointmentOverrunDetail = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<AppointmentOverrunDetailResult> =>
  schedulingDetailGet('appointment-overrun', params, options)

export interface PatientFlowOverviewColumn {
  header: string
  count: number
  names: string[]
}

export interface PatientFlowOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  columns: PatientFlowOverviewColumn[]
}

export interface PatientFlowKpiData {
  scheduledToday: number
  scheduledGoal: number | null
  checkedIn: number
  inTreatment: number
  checkedOut: number
  noShows: number
  avgWaitTime: string
  comparisonLabel: string
  scheduledDelta: string
  scheduledTrend: 'up' | 'down'
  noShowDelta: string
  noShowTrend: 'up' | 'down'
  waitTimeDelta: string
  waitTimeTrend: 'up' | 'down'
}

export interface PatientFlowChartPoint {
  label: string
  scheduled: number
  checkedIn: number
  checkedOut: number
}

export interface PatientFlowChartConfig {
  data: PatientFlowChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface PatientFlowPatientRow {
  id: string
  patientId?: string
  patientName: string
  appointmentTime: string
  provider: string
  operatory: string
  status: string
  waitTime: string
}

export interface PatientFlowByProviderRow {
  id: string
  provider: string
  scheduled: number
  checkedIn: number
  checkedOut: number
  noShows: number
  avgWaitTime: string
  utilizationRate: string
}

export interface PatientFlowByTimePeriodRow {
  id: string
  timePeriod: string
  scheduled: number
  checkedIn: number
  checkedOut: number
  noShows: number
  avgWaitTime: string
  showUpRate: string
}

export interface PatientFlowDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: PatientFlowKpiData
  chart: PatientFlowChartConfig
  patients: PatientFlowPatientRow[]
  byProvider: PatientFlowByProviderRow[]
  byTimePeriod: PatientFlowByTimePeriodRow[]
}

export const getPatientFlowOverview = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<PatientFlowOverviewResult> =>
  schedulingDetailGet('patient-flow/overview', params, options)

export interface PatientFlowPatientsResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: PatientFlowPatientRow[]
  count: number
}

export const getPatientFlowDetail = (
  params: SchedulingOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<PatientFlowDetailResult> =>
  schedulingDetailGet('patient-flow/detail', params, options)

export const getPatientFlowPatients = (
  params: SchedulingOverviewQueryParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<PatientFlowPatientsResult> =>
  schedulingDetailGet('patient-flow/patients', params, options)
